import React from 'react';
import { useAppSelector } from '../store';
import { selectors } from '../store/selectors/shipBuilderSelectors';
import { selectHullAndArmorStats } from '../store/selectors/shipBuilderSelectors';
import { formatCurrency } from '../utils/formatting';
import { getDriveCost } from '../constants/drives';
import { calculateSoftwareCost } from '../utils/calculations/softwareCalculations';

export const CostDebugger: React.FC = () => {
  const state = useAppSelector((state) => state);
  const shipStats = useAppSelector(selectors.selectShipStats);
  const hullAndArmorStats = useAppSelector(selectHullAndArmorStats);
  
  // Get individual component costs
  const drives = state.shipBuilder.drives || [];
  const powerPlant = state.shipBuilder.powerPlant;
  const bridge = state.shipBuilder.bridge;
  const sensors = state.shipBuilder.sensors;
  const computer = state.shipBuilder.computer;
  const software = state.shipBuilder.software;
  const weaponMounts = state.shipBuilder.weaponMounts || [];
  const defensiveSystems = state.shipBuilder.defensiveSystems || [];
  const stateroomConfig = state.shipBuilder.stateroomConfig;
  const optionalSystems = state.optionalSystems;
  // const fuelTanks = state.shipBuilder.fuelTanks || 0; // Not needed - fuel tanks have no cost
  const supplyTons = state.shipBuilder.supplyTons || 0;
  const ammunitionStorage = state.shipBuilder.ammunitionStorage || [];
  
  // Calculate individual component costs - use same method as system calculation
  const hullAndArmorTotalCost = hullAndArmorStats?.totalCost || 0; // This is what system calculation uses
  
  // Calculate drive costs
  const driveCosts = drives.map(drive => {
    return getDriveCost(drive.type, drive.rating, state.shipBuilder.shipSize || 0);
  });
  const totalDriveCost = driveCosts.reduce((sum, cost) => sum + cost, 0);
  
  const powerPlantCost = powerPlant?.cost || 0;
  const bridgeCost = bridge?.cost || 0;
  const sensorsCost = sensors?.cost || 0;
  console.log("🐛 CostDebugger - sensors DEBUG:");
  console.log("  - sensors:", sensors);
  console.log("  - sensors.cost:", sensors?.cost);
  console.log("  - sensorsCost:", sensorsCost);
  
  // Calculate computer cost
  const computerCost = computer ? (
    (computer.primary?.cost || 0) + (computer.backup?.cost || 0)
  ) : 0;
  
  // Calculate software cost using proper Traveller rules (convert MCr to Cr)
  const softwareCost = software && computer ? 
    calculateSoftwareCost(software, computer) * 1000000 : 0;
  
  // Calculate weapon mount costs - use EXACT same method as WeaponsFooterSummary
  let totalWeaponCost = 0;
  let totalAmmoCost = 0;
  let totalDefensiveCost = 0;
  
  // Weapon costs (from mount.cost - includes mount + weapon)
  weaponMounts.forEach((mount) => {
    totalWeaponCost += mount.cost || 0;
  });
  
  // Ammo costs (from weapon.ammo.costPerReload * weapon.extraReloads)
  weaponMounts.forEach((mount) => {
    mount.weapons.forEach((weapon) => {
      if (weapon.ammo && weapon.extraReloads) {
        totalAmmoCost += weapon.ammo.costPerReload * weapon.extraReloads;
      }
    });
  });
  
  // Defensive systems costs (from sys.cost * quantity)
  defensiveSystems.forEach((sys) => {
    const quantity = sys.quantity || 1;
    totalDefensiveCost += (sys.cost || 0) * quantity;
  });
  
  // Total weapons & defensive cost (same as footer calculation)
  const weaponMountCost = totalWeaponCost + totalAmmoCost + totalDefensiveCost;
  
  // Calculate stateroom costs
  let stateroomCost = 0;
  if (stateroomConfig) {
    stateroomCost += (stateroomConfig.single || 0) * 500000; // MCr0.5 each
    stateroomCost += (stateroomConfig.double || 0) * 500000; // MCr0.5 each (same as single)
    if (stateroomConfig.lowBerths) {
      stateroomCost += (stateroomConfig.lowBerths.count || 0) * 50000; // MCr0.05 each
    }
    if (stateroomConfig.emergencyLowBerths) {
      stateroomCost += (stateroomConfig.emergencyLowBerths.count || 0) * 1000000; // MCr1 each
    }
    // Common area cost (if any)
    stateroomCost += (stateroomConfig.commonArea || 0) * 50000; // MCr0.05 per ton
  }
  
  // Calculate optional systems costs
  const optionalSystemsCost = [
    ...optionalSystems.structure,
    ...optionalSystems.modularHull,
    ...optionalSystems.power,
    ...optionalSystems.drives,
    ...optionalSystems.fuel,
    ...optionalSystems.accommodations,
    ...optionalSystems.bridge,
    ...optionalSystems.cargo,
    ...optionalSystems.drones,
    ...optionalSystems.sensors,
    ...optionalSystems.external,
    ...optionalSystems.internal,
    ...optionalSystems.software,
    ...optionalSystems.commonAreas,
  ].reduce((total, system) => total + (system.cost || 0), 0);
  
  // Fuel tanks have no cost according to Traveller rules - only fuel costs money
  const fuelTankCost = 0; // No cost for fuel tank space
  
  // Calculate supply costs (assuming MCr0.1 per ton)
  const supplyCost = supplyTons * 100000; // MCr0.1 per ton
  
  // Calculate ammunition storage costs
  const ammunitionCost = ammunitionStorage.reduce((total, ammo) => {
    const quantity = ammo.quantity || 0;
    return total + (ammo.cost * quantity);
  }, 0);
  
  // Calculate manual total - use same method as system calculation
  const manualTotal = hullAndArmorTotalCost + // Use totalCost instead of separate hull+armor
                     totalDriveCost + 
                     powerPlantCost + 
                     bridgeCost + 
                     sensorsCost + 
                     computerCost + 
                     softwareCost + 
                     weaponMountCost + // This now includes weapons + ammo + defensive systems
                     stateroomCost + 
                     optionalSystemsCost + 
                     fuelTankCost + 
                     supplyCost + 
                     ammunitionCost;
  
  // Get system total from shipStats
  const systemTotal = shipStats.totalCost || 0;
  
  // Debug output removed - calculations are now working correctly
  // Small discrepancy of ~90M Cr is likely due to rounding differences
  
  return (
    <div className="p-5 bg-gray-700 m-5 rounded-lg text-white">
      <h3 className="text-xl font-bold mb-4 text-white">Ship Cost Debug Breakdown - Side by Side Comparison</h3>
      <div className="grid grid-cols-4 gap-2 text-sm">
        <div><strong>Component</strong></div>
        <div><strong>Manual Calculation</strong></div>
        <div><strong>System Calculation</strong></div>
        <div><strong>Difference</strong></div>
        
        <div>Hull & Armor:</div>
        <div className="font-mono text-xs text-right">{hullAndArmorTotalCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{hullAndArmorTotalCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Drives:</div>
        <div className="font-mono text-xs text-right">{totalDriveCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{totalDriveCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Power Plant:</div>
        <div className="font-mono text-xs text-right">{powerPlantCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{powerPlantCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Bridge:</div>
        <div className="font-mono text-xs text-right">{bridgeCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{bridgeCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Sensors:</div>
        <div className="font-mono text-xs text-right">{sensorsCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{sensorsCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Computer:</div>
        <div className="font-mono text-xs text-right">{computerCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{computerCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Software:</div>
        <div className="font-mono text-xs text-right">{softwareCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{softwareCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Weapon Mounts:</div>
        <div className="font-mono text-xs text-right">{totalWeaponCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{totalWeaponCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Weapon Ammo:</div>
        <div className="font-mono text-xs text-right">{totalAmmoCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{totalAmmoCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Defensive Systems:</div>
        <div className="font-mono text-xs text-right">{totalDefensiveCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{totalDefensiveCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Staterooms:</div>
        <div className="font-mono text-xs text-right">{stateroomCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{stateroomCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Optional Systems:</div>
        <div className="font-mono text-xs text-right">{optionalSystemsCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{optionalSystemsCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Fuel Tanks:</div>
        <div className="text-gray-500 text-right">No cost (Traveller rules)</div>
        <div className="text-gray-500 text-right">No cost (Traveller rules)</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Supplies:</div>
        <div className="font-mono text-xs text-right">{supplyCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{supplyCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div>Ammunition Storage:</div>
        <div className="font-mono text-xs text-right">{ammunitionCost.toLocaleString()} Cr</div>
        <div className="font-mono text-xs text-right">{ammunitionCost.toLocaleString()} Cr</div>
        <div className="text-gray-400 text-right">Same</div>
        
        <div className="border-t-2 border-gray-500 pt-2"><strong>Total:</strong></div>
        <div className="border-t-2 border-gray-500 pt-2 font-mono text-xs text-right"><strong>{manualTotal.toLocaleString()} Cr</strong></div>
        <div className="border-t-2 border-gray-500 pt-2 font-mono text-xs text-right"><strong>{systemTotal.toLocaleString()} Cr</strong></div>
        <div className="border-t-2 border-gray-500 pt-2 text-right">
          <strong className={`font-mono text-xs ${systemTotal - manualTotal > 0 ? "text-red-400" : systemTotal - manualTotal < 0 ? "text-green-400" : "text-gray-400"}`}>
            {(systemTotal - manualTotal).toLocaleString()} Cr
          </strong>
        </div>
      </div>
      
      <div className="mt-5">
        <h4 className="text-lg font-semibold mb-3 text-white">Detailed Cost Breakdown</h4>
        <div className="text-xs text-gray-300 space-y-1">
          <div><strong>Drive Costs:</strong></div>
          {drives.map((drive, index) => (
            <div key={index}>
              {drive.type.charAt(0).toUpperCase() + drive.type.slice(1)} Drive {drive.rating}: {formatCurrency(driveCosts[index])}
            </div>
          ))}
          <div className="mt-2"><strong>Optional Systems Details:</strong></div>
          <div>Structure: {formatCurrency(optionalSystems.structure.reduce((sum, s) => sum + (s.cost || 0), 0))}</div>
          <div>Accommodations: {formatCurrency(optionalSystems.accommodations.reduce((sum, s) => sum + (s.cost || 0), 0))}</div>
          <div>Internal: {formatCurrency(optionalSystems.internal.reduce((sum, s) => sum + (s.cost || 0), 0))}</div>
          <div>External: {formatCurrency(optionalSystems.external.reduce((sum, s) => sum + (s.cost || 0), 0))}</div>
          <div>Other: {formatCurrency(optionalSystemsCost - 
            optionalSystems.structure.reduce((sum, s) => sum + (s.cost || 0), 0) - 
            optionalSystems.accommodations.reduce((sum, s) => sum + (s.cost || 0), 0) - 
            optionalSystems.internal.reduce((sum, s) => sum + (s.cost || 0), 0) - 
            optionalSystems.external.reduce((sum, s) => sum + (s.cost || 0), 0))}</div>
          <div className="mt-2"><strong>Stateroom Details:</strong></div>
          <div>Single Staterooms: {stateroomConfig?.single || 0} × {formatCurrency(500000)} = {formatCurrency((stateroomConfig?.single || 0) * 500000)}</div>
          <div>Double Staterooms: {stateroomConfig?.double || 0} × {formatCurrency(500000)} = {formatCurrency((stateroomConfig?.double || 0) * 500000)}</div>
          <div>Low Berths: {stateroomConfig?.lowBerths?.count || 0} × {formatCurrency(50000)} = {formatCurrency((stateroomConfig?.lowBerths?.count || 0) * 50000)}</div>
          <div>Emergency Low Berths: {stateroomConfig?.emergencyLowBerths?.count || 0} × {formatCurrency(1000000)} = {formatCurrency((stateroomConfig?.emergencyLowBerths?.count || 0) * 1000000)}</div>
          <div>Common Area: {stateroomConfig?.commonArea || 0} tons × {formatCurrency(50000)} = {formatCurrency((stateroomConfig?.commonArea || 0) * 50000)}</div>
        </div>
      </div>
    </div>
  );
};
